import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'

import { useOutsideClick, useSetState } from '@campaignhub/react-hooks'

import {
  Box, Button, FormField, Text,
} from '@campaignhub/suit-theme'

import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import custom from '@styles/custom.module.scss'

const formatRange = (start, end) => (
  `${DateTime.fromJSDate(start).toFormat('dd MMM yyyy')} - ${DateTime.fromJSDate(end).toFormat('dd MMM yyyy')}`
)

const defaultState = {
  showFilter: false,
  filterLabel: '',
}

const presetRanges = {
  Today: () => ({
    dateStart: DateTime.now().startOf('day').toJSDate(),
    dateEnd: DateTime.now().plus({ days: 1 }).startOf('day').toJSDate(),
  }),
  Yesterday: () => ({
    dateStart: DateTime.now().minus({ days: 1 }).startOf('day').toJSDate(),
    dateEnd: DateTime.now().startOf('day').toJSDate(),
  }),
  Tomorrow: () => ({
    dateStart: DateTime.now().plus({ days: 1 }).startOf('day').toJSDate(),
    dateEnd: DateTime.now().plus({ days: 2 }).startOf('day').toJSDate(),
  }),
  'This Month': () => ({
    dateStart: DateTime.now().startOf('month').toJSDate(),
    dateEnd: DateTime.now().endOf('month').toJSDate(),
  }),
  'Past Month': () => ({
    dateStart: DateTime.now().minus({ months: 1 }).startOf('month').toJSDate(),
    dateEnd: DateTime.now().minus({ months: 1 }).endOf('month').toJSDate(),
  }),
  Reset: () => ({
    dateStart: DateTime.now().startOf('day').toJSDate(),
    dateEnd: DateTime.now().plus({ months: 1 }).endOf('day').toJSDate(),
  }),
}

const DateRangeFilter = (props) => {
  const { dateStart, dateEnd, callbacks: { setDateRange } } = props

  const [state, setState] = useSetState(defaultState)
  const { showFilter, filterLabel } = state

  const contentEl = useRef()
  const [isClickedOutside, setIsClickedOutside] = useOutsideClick(contentEl, { enabled: showFilter })

  useEffect(() => {
    if (isClickedOutside && showFilter){
      setState({ showFilter: !showFilter })
      setIsClickedOutside(false)
    }
  }, [isClickedOutside])

  useEffect(() => {
    setState({ filterLabel: formatRange(dateStart, dateEnd) })
  }, [])

  const applyPreset = (label) => {
    const range = presetRanges[label]()
    setDateRange(range)
    setState({ filterLabel: label === 'Reset' ? formatRange(range.dateStart, range.dateEnd) : label })
  }

  const applyCustomRange = (start, end) => {
    const normalizedStart = DateTime.fromJSDate(start).startOf('day').toJSDate()
    const normalizedEnd = DateTime.fromJSDate(end).endOf('day').toJSDate()
    setDateRange({ dateStart: normalizedStart, dateEnd: normalizedEnd })
    setState({ filterLabel: formatRange(normalizedStart, normalizedEnd) })
  }

  return (
    <FormField direction="column">
      <Box className={custom.root} ref={contentEl}>
        <select
          onMouseDown={(e) => {
            e.preventDefault()
            setState({ showFilter: !showFilter })
          }}
          defaultValue="Select Date Range"
          style={{
            color: '#595959',
            padding: '8px 6px',
            border: '1px solid #E5E7EB',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            width: '100%',
            height: 'auto',
            margin: '12px 14px',
            marginBottom: 0,
            boxSizing: 'border-box',
            cursor: 'pointer',
          }}
        >
          <option>{filterLabel}</option>
        </select>

        {showFilter && (
          <Box className={custom.popup}>
            <Box flexDirection="column" maxHeight="290px" overflowY="auto">
              <Box flexDirection="row" marginBottom="medium">
                <Box flexDirection="column" marginRight="medium">
                  <Text color="bodyFontLightColor" fontSize="xsmall" paddingBottom="small">From</Text>
                  <DatePicker
                    selected={dateStart}
                    onChange={date => applyCustomRange(date, dateEnd)}
                    dateFormat="dd MMM yyyy"
                    maxDate={dateEnd}
                  />
                </Box>
                <Box flexDirection="column" marginRight="medium">
                  <Text color="bodyFontLightColor" fontSize="xsmall" paddingBottom="small">To</Text>
                  <DatePicker
                    selected={dateEnd}
                    onChange={date => applyCustomRange(dateStart, date)}
                    dateFormat="dd MMM yyyy"
                    minDate={dateStart}
                  />
                </Box>
              </Box>
              <Button buttonStyle="secondary" size="medium" marginBottom="medium" fontWeight="light" onClick={() => applyPreset('Today')}>
                Today
              </Button>
              <Button buttonStyle="secondary" size="medium" marginBottom="medium" fontWeight="light" onClick={() => applyPreset('Yesterday')}>
                Yesterday
              </Button>
              <Button buttonStyle="secondary" size="medium" marginBottom="medium" fontWeight="light" onClick={() => applyPreset('Tomorrow')}>
                Tomorrow
              </Button>
              <Button buttonStyle="secondary" size="medium" marginBottom="medium" fontWeight="light" onClick={() => applyPreset('This Month')}>
                This Month
              </Button>
              <Button buttonStyle="secondary" size="medium" marginBottom="medium" fontWeight="light" onClick={() => applyPreset('Past Month')}>
                Past Month
              </Button>
              <Button buttonStyle="secondary" size="medium" marginBottom="medium" fontWeight="light" onClick={() => applyPreset('Reset')}>
                Reset
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </FormField>
  )
}

DateRangeFilter.propTypes = {
  dateStart: PropTypes.instanceOf(Date).isRequired,
  dateEnd: PropTypes.instanceOf(Date).isRequired,
  callbacks: PropTypes.shape({
    setDateRange: PropTypes.func.isRequired,
  }).isRequired,
}

export default DateRangeFilter
